import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  type User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/config/firebase'
import { CASINO_CONFIG } from '@/config/casino'
import type { User } from '@/types'

interface AuthContextType {
  user: User | null
  firebaseUser: FirebaseUser | null
  isLoading: boolean
  isDemo: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  isPatriarch: boolean
  error: string | null
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>
  playAsGuest: () => Promise<{ success: boolean; error?: string }>
  register: (name: string, email: string, password: string, referralCode?: string) => Promise<{ success: boolean; error?: string; requiresVerification?: boolean; isDemo?: boolean }>
  logout: () => Promise<void>
  sendPasswordReset: (email: string) => Promise<{ success: boolean; error?: string }>
  requestMembership: () => Promise<{ success: boolean; error?: string }>
  reloadDemoBalance: () => Promise<boolean>
  updateBalance: (amount: number) => Promise<boolean>
  refreshUserData: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // isDemo is computed based on membershipStatus
  const isDemo = user?.membershipStatus === 'demo'

  // Load user data from Firestore
  const loadUserData = async (uid: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid))
      if (userDoc.exists()) {
        const data = userDoc.data()
        return {
          uid,
          email: data.email,
          displayName: data.name || firebaseUser?.displayName || null,
          name: data.name,
          balance: data.balance || 0,
          demoBalance: data.demoBalance || CASINO_CONFIG.demoBalance,
          isAdmin: data.isAdmin || false,
          isPatriarch: data.isPatriarch || false,
          emailVerified: data.emailVerified || false,
          membershipStatus: data.membershipStatus || 'pending',
          referredBy: data.referredBy || null,
          referredByName: data.referredByName || null,
          referralCode: data.referralCode || null,
          kycStatus: data.kycStatus,
          pendingWithdrawal: data.pendingWithdrawal || 0,
          totalDeposited: data.totalDeposited || 0,
          totalWithdrawn: data.totalWithdrawn || 0,
          stats: data.stats,
          createdAt: data.createdAt?.toDate?.() || new Date(),
        }
      }
      return null
    } catch (err) {
      console.error('Error loading user data:', err)
      return null
    }
  }

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser)

      if (fbUser) {
        const userData = await loadUserData(fbUser.uid)
        setUser(userData)
      } else {
        setUser(null)
      }

      setIsLoading(false)
    })

    return unsubscribe
  }, [])

  const login = async (email: string, password: string) => {
    try {
      setError(null)
      console.log('Attempting login for:', email) // Debug
      const result = await signInWithEmailAndPassword(auth, email, password)
      console.log('Login successful, uid:', result.user.uid) // Debug

      // Check membership status
      const userDoc = await getDoc(doc(db, 'users', result.user.uid))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        // Demo users can login
        if (userData.membershipStatus === 'demo') {
          return { success: true }
        }
        // Pending users cannot login
        if (userData.membershipStatus === 'pending') {
          await signOut(auth)
          return { success: false, error: 'Tu solicitud de membresía está pendiente de aprobación.' }
        }
        // Rejected users cannot login
        if (userData.membershipStatus === 'rejected') {
          await signOut(auth)
          return { success: false, error: 'Tu solicitud de membresía fue rechazada.' }
        }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = getErrorMessage((err as { code?: string }).code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  const loginWithGoogle = async () => {
    try {
      setError(null)
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)

      // Check if new user
      const userDoc = await getDoc(doc(db, 'users', result.user.uid))
      if (!userDoc.exists()) {
        // Create new user document
        await setDoc(doc(db, 'users', result.user.uid), {
          name: result.user.displayName,
          email: result.user.email,
          balance: 0,
          demoBalance: CASINO_CONFIG.demoBalance,
          createdAt: serverTimestamp(),
          isAdmin: false,
          emailVerified: true,
          membershipStatus: 'approved',
        })
      }

      return { success: true }
    } catch (err) {
      const errorMessage = getErrorMessage((err as { code?: string }).code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Play as guest - creates anonymous demo account
  const playAsGuest = async () => {
    try {
      setError(null)

      // Generate random credentials for anonymous user
      const randomId = Math.random().toString(36).substring(2, 10)
      const guestEmail = `guest_${randomId}@demo.cosanostra.casino`
      const guestPassword = `Demo${randomId}!${Date.now()}`
      const guestName = `Invitado_${randomId.substring(0, 4).toUpperCase()}`

      // Create account
      const result = await createUserWithEmailAndPassword(auth, guestEmail, guestPassword)

      // Update profile
      await updateProfile(result.user, { displayName: guestName })

      // Create user document as demo
      await setDoc(doc(db, 'users', result.user.uid), {
        name: guestName,
        email: guestEmail,
        balance: 0,
        demoBalance: CASINO_CONFIG.demoBalance,
        createdAt: serverTimestamp(),
        isAdmin: false,
        emailVerified: false,
        membershipStatus: 'demo',
        isGuest: true,
      })

      // Load user data
      const userData = await loadUserData(result.user.uid)
      setUser(userData)

      return { success: true }
    } catch (err) {
      console.error('Error creating guest account:', err)
      const errorMessage = getErrorMessage((err as { code?: string }).code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  const register = async (name: string, email: string, password: string, referralCode?: string) => {
    try {
      setError(null)
      console.log('Registering user:', email, 'with referral:', referralCode || 'none') // Debug

      const result = await createUserWithEmailAndPassword(auth, email, password)
      console.log('User created in Firebase Auth, uid:', result.user.uid) // Debug

      // Update profile with name
      await updateProfile(result.user, { displayName: name })

      // Determine membership status based on referral code
      // No code = demo account (can play for free)
      // With code = pending (needs approval for real play)
      const membershipStatus = referralCode ? 'pending' : 'demo'
      const isDemoAccount = !referralCode

      // Create user document in Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        name,
        email,
        balance: 0,
        demoBalance: CASINO_CONFIG.demoBalance,
        createdAt: serverTimestamp(),
        isAdmin: false,
        emailVerified: false,
        membershipStatus,
        referralCode: referralCode || null,
      })

      // Demo accounts don't need email verification, they can play immediately
      if (isDemoAccount) {
        // Reload user data
        const userData = await loadUserData(result.user.uid)
        setUser(userData)
        return { success: true, requiresVerification: false, isDemo: true }
      }

      return { success: true, requiresVerification: true, isDemo: false }
    } catch (err) {
      const errorMessage = getErrorMessage((err as { code?: string }).code)
      setError(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  const logout = async () => {
    setUser(null)
    await signOut(auth)
  }

  const sendPasswordReset = async (email: string) => {
    try {
      console.log('Sending password reset to:', email) // Debug
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin,
        handleCodeInApp: false,
      })
      console.log('Password reset email sent successfully') // Debug
      return { success: true }
    } catch (err) {
      console.error('Password reset error:', err) // Debug
      const errorMessage = getErrorMessage((err as { code?: string }).code)
      return { success: false, error: errorMessage }
    }
  }

  // Request membership for demo users - changes status to pending
  const requestMembership = async () => {
    if (!user || !firebaseUser) {
      return { success: false, error: 'No hay usuario logueado' }
    }

    if (user.membershipStatus !== 'demo') {
      return { success: false, error: 'Solo usuarios demo pueden solicitar membresía' }
    }

    try {
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        membershipStatus: 'pending',
        membershipRequestedAt: serverTimestamp(),
      }, { merge: true })

      // Refresh user data
      const userData = await loadUserData(firebaseUser.uid)
      setUser(userData)

      return { success: true }
    } catch (err) {
      console.error('Error requesting membership:', err)
      return { success: false, error: 'Error al solicitar membresía' }
    }
  }

  // Reload demo balance to initial value
  const reloadDemoBalance = async (): Promise<boolean> => {
    if (!user || !firebaseUser || user.membershipStatus !== 'demo') {
      return false
    }

    try {
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        demoBalance: CASINO_CONFIG.demoBalance,
      }, { merge: true })

      setUser({ ...user, demoBalance: CASINO_CONFIG.demoBalance })
      return true
    } catch (err) {
      console.error('Error reloading demo balance:', err)
      return false
    }
  }

  const updateBalance = async (amount: number): Promise<boolean> => {
    if (!user || !firebaseUser) return false

    const currentBalance = isDemo ? (user.demoBalance || 0) : (user.balance || 0)
    const newBalance = currentBalance + amount
    if (newBalance < 0) return false

    try {
      const updateField = isDemo ? 'demoBalance' : 'balance'
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        [updateField]: newBalance,
      }, { merge: true })

      if (isDemo) {
        setUser({ ...user, demoBalance: newBalance })
      } else {
        setUser({ ...user, balance: newBalance })
      }
      return true
    } catch (err) {
      console.error('Error updating balance:', err)
      return false
    }
  }

  const refreshUserData = async () => {
    if (firebaseUser && !isDemo) {
      const userData = await loadUserData(firebaseUser.uid)
      setUser(userData)
    }
  }

  const value: AuthContextType = {
    user,
    firebaseUser,
    isLoading,
    isDemo,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    isPatriarch: user?.isPatriarch || false,
    error,
    login,
    loginWithGoogle,
    playAsGuest,
    register,
    logout,
    sendPasswordReset,
    requestMembership,
    reloadDemoBalance,
    updateBalance,
    refreshUserData,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper to translate Firebase error codes
function getErrorMessage(code?: string): string {
  console.log('Firebase error code:', code) // Debug logging
  const errors: Record<string, string> = {
    'auth/email-already-in-use': 'Este email ya está registrado',
    'auth/invalid-email': 'Email inválido',
    'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
    'auth/user-not-found': 'Usuario no encontrado. ¿Ya te registraste?',
    'auth/wrong-password': 'Contraseña incorrecta',
    'auth/too-many-requests': 'Demasiados intentos. Intentá más tarde',
    'auth/popup-closed-by-user': 'Ventana cerrada',
    'auth/network-request-failed': 'Error de conexión. Verificá tu internet',
    'auth/requires-recent-login': 'Por seguridad, volvé a iniciar sesión para continuar',
    'auth/invalid-credential': 'Email o contraseña incorrectos',
    'auth/user-disabled': 'Esta cuenta ha sido deshabilitada',
    'auth/operation-not-allowed': 'Operación no permitida',
    'auth/account-exists-with-different-credential': 'Ya existe una cuenta con este email usando otro método de inicio de sesión',
    'auth/invalid-action-code': 'El enlace ha expirado o ya fue usado',
    'auth/expired-action-code': 'El enlace ha expirado',
  }
  return errors[code || ''] || `Error: ${code || 'desconocido'}`
}
